// Ficha lateral de una carga: se abre al pulsar su fila. Una sola columna, con
// el diseño que pasó Víctor: cabecera (carga, cuándo sale, cliente y pedido con
// enlace a ventas), tres KPIs, el recorrido en seis fases, las acciones, los
// detalles y las facturas plegados, y la actividad (el chat de la carga) al pie.
//
// El generador de documentos se abre como cajón ENCIMA de la ficha y se monta
// aquí, como hermano del `Drawer` y no dentro: el panel de un `Drawer` lleva
// `transform`, y un ancestro transformado se convierte en el bloque contenedor de
// todo `fixed` descendiente — el generador se pintaría encajado en 560 px.
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Drawer } from '@/components/common/Drawer'
import { CargaDocumentosDrawer, EMPRESAS_CON_PACKING } from '@/components/pedidos-ventas/CargaDocumentosDrawer'
import { useFacturasCarga } from '@/hooks/useCargasSalida'
import { useModulos } from '@/hooks/useModulos'
import type { CargaSalida } from '@/types'
import { ESTADO_CHIP, estadoPickingDe, fechaDe } from '@/utils/cargasSalida'
import { salidaRelativa } from '@/utils/fichaCarga'
import { ChatCargaPanel } from './ChatCargaPanel'
import { FichaDocumentos } from './ficha/FichaDocumentos'
import { FichaEstadoManual } from './ficha/FichaEstadoManual'
import { FichaDetalles, FichaKpis, Plegable } from './ficha/FichaResumen'
import { StepperFases } from './ficha/StepperFases'

interface Props {
  c: CargaSalida
  dark: boolean
  onClose: () => void
  /** Cierra la ficha y lleva a la pestaña «Back orders» de la pantalla. */
  onVerBackOrders: () => void
}

export function FichaCargaDrawer({ c, dark, onClose, onVerBackOrders }: Props) {
  const { t } = useTranslation()
  const { puede } = useModulos()
  const [generadorAbierto, setGeneradorAbierto] = useState(false)
  const [facturasAbiertas, setFacturasAbiertas] = useState(false)
  const facturasRef = useRef<HTMLDivElement>(null)
  const { facturas } = useFacturasCarga(c.carga)

  const salida = salidaRelativa(fechaDe(c.fechaEnvioPrevista))
  const cuando = salida.tipo === 'dias' || salida.tipo === 'retraso'
    ? t(`cargasSalida.ficha.salida.${salida.tipo}`, { count: salida.n })
    : t(`cargasSalida.ficha.salida.${salida.tipo}`)
  const urgente = salida.tipo === 'hoy' || salida.tipo === 'retraso'
  const borde = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const numPedido = c.pedidos[0]
  const generador = EMPRESAS_CON_PACKING.has(c.empresa.toLowerCase()) && !!numPedido
    ? () => setGeneradorAbierto(true)
    : null
  // El enlace a ventas sólo a quien abre ese módulo: logística no lo abre, y
  // un enlace a una pantalla que da 403 es peor que el número a secas.
  const verVentas = puede('ventas')

  function verFacturas() {
    setFacturasAbiertas(true)
    requestAnimationFrame(() => facturasRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }))
  }

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        dark={dark}
        width="w-screen sm:w-[480px]"
        ariaLabel={t('cargasSalida.ficha.titulo', { carga: c.carga })}
      >
        {cerrar => (
          <>
            <header className={`px-5 pt-4 pb-3 border-b shrink-0 ${borde}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold font-mono uppercase tracking-wide text-slate-500">
                    {c.carga} · <span className={urgente ? (dark ? 'text-rose-400' : 'text-rose-600') : ''}>{cuando}</span>
                  </p>
                  <h2
                    className={`text-[18px] font-black leading-tight tracking-tight truncate ${dark ? 'text-white' : 'text-slate-900'}`}
                    title={c.clienteNombre ?? c.cliente ?? undefined}
                  >
                    {c.clienteNombre || c.cliente || '—'}
                  </h2>
                  {c.pedidos.map(p => (
                    <p key={p} className={`text-[12px] mt-0.5 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {verVentas ? (
                        <Link
                          to={`/pedidos-ventas/${encodeURIComponent(c.empresa)}/${encodeURIComponent(p)}`}
                          className="text-sky-600 underline underline-offset-2 hover:text-sky-500"
                        >
                          {t('cargasSalida.ficha.pedido', { pedido: p })} · {t('cargasSalida.ficha.verEnVentas')}
                        </Link>
                      ) : t('cargasSalida.ficha.pedido', { pedido: p })}
                    </p>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={cerrar}
                  aria-label={t('cargasSalida.ficha.cerrar')}
                  title={t('cargasSalida.ficha.cerrar')}
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border cursor-pointer transition-colors ${
                    dark ? 'border-white/10 text-slate-400 hover:text-white hover:bg-white/10' : 'border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <span className={`mt-2 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                dark ? ESTADO_CHIP[c.estado].dark : ESTADO_CHIP[c.estado].light
              }`}>
                {t(`cargasSalida.estadoCarga.${c.estado}`)}
              </span>
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-5 py-4 flex flex-col gap-4">
              <FichaKpis c={c} dark={dark} />
              <StepperFases
                c={c}
                dark={dark}
                facturas={facturas.length}
                onPacking={generador}
                onFacturas={verFacturas}
              />
              <FichaEstadoManual c={c} dark={dark} onGenerarDocumentos={generador} />

              <div className="flex flex-col gap-2">
                <FichaDetalles c={c} dark={dark} onVerBackOrders={() => { cerrar(); onVerBackOrders() }} />
                <div ref={facturasRef}>
                  <Plegable
                    dark={dark}
                    titulo={t('cargasSalida.ficha.documentos.facturas')}
                    abierto={facturasAbiertas}
                    onToggle={setFacturasAbiertas}
                  >
                    {/* Montado sólo abierto: cada adjunto es una llamada al flujo. */}
                    {facturasAbiertas && <FichaDocumentos c={c} dark={dark} />}
                  </Plegable>
                </div>
              </div>

              <section className={`border-t pt-3 ${borde}`}>
                <h3 className="text-[11px] font-bold uppercase tracking-wider mb-2 text-slate-500">
                  {t('cargasSalida.ficha.actividad')}
                </h3>
                <div className={`h-[380px] rounded-lg border overflow-hidden ${borde}`}>
                  <ChatCargaPanel carga={c.carga} dark={dark} enfocar={false} />
                </div>
              </section>
            </div>
          </>
        )}
      </Drawer>

      {generadorAbierto && numPedido && (
        <CargaDocumentosDrawer
          dark={dark}
          empresa={c.empresa}
          numPedido={numPedido}
          carga={c.carga}
          estado={estadoPickingDe(c)}
          paletizada={c.paletizada}
          aerea={c.aerea}
          contextoAlbaran={{ pedido: numPedido, destinatario: c.clienteNombre, destino: c.destino }}
          onClose={() => setGeneradorAbierto(false)}
        />
      )}
    </>
  )
}
